import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_TOTAL_MB,
  MAX_TOTAL_BYTES,
  ImagesToPdfError,
  imagesToPdf,
  isSupportedImage,
  outputFilename,
  totalBytes,
  type ImageFileItem,
  type ImageToPdfMode,
} from '../model/lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'

type Phase =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; blob: Blob; objectUrl: string; filename: string; durationMs: number }
  | { kind: 'error'; message: string; errorCode: string }

interface ModeConfig {
  mode: ImageToPdfMode
  utilityKey: 'jpgVPdf' | 'png-v-pdf' | 'heicVPdf'
  copyKey: 'jpgVPdf' | 'png-v-pdf' | 'heicVPdf'
  accept: string
  fallbackFilename: string
  dropLabel: string
}

const MODE_CONFIG = {
  jpg: {
    mode: 'jpg',
    utilityKey: 'jpgVPdf',
    copyKey: 'jpgVPdf',
    accept: 'image/jpeg,image/png,.jpg,.jpeg,.png',
    fallbackFilename: 'images.pdf',
    dropLabel: 'IMG',
  },
  png: {
    mode: 'png',
    utilityKey: 'png-v-pdf',
    copyKey: 'png-v-pdf',
    accept: 'image/png,image/jpeg,.png,.jpg,.jpeg',
    fallbackFilename: 'png-images.pdf',
    dropLabel: 'PNG',
  },
  heic: {
    mode: 'heic',
    utilityKey: 'heicVPdf',
    copyKey: 'heicVPdf',
    accept: 'image/heic,image/heif,.heic,.heif',
    fallbackFilename: 'heic-images.pdf',
    dropLabel: 'HEIC',
  },
} as const satisfies Record<ImageToPdfMode, ModeConfig>

interface ImageToPdfFormProps {
  mode: ImageToPdfMode
}

function createImageItem(file: File, mode: ImageToPdfMode): ImageFileItem {
  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
    file,
    previewUrl: mode === 'heic' ? null : URL.createObjectURL(file),
  }
}

function cleanupItem(item: ImageFileItem) {
  if (item.previewUrl) {
    URL.revokeObjectURL(item.previewUrl)
  }
}

function errorMessage(
  copy: Translations['jpgVPdf'],
  error: unknown,
): { code: string; message: string } {
  if (error instanceof ImagesToPdfError && error.code === 'unsupported_browser') {
    return {
      code: 'unsupported_browser',
      message: copy.unsupportedBrowser,
    }
  }

  return {
    code: 'conversion_error',
    message: copy.conversionError,
  }
}

export function ImageToPdfForm({ mode }: ImageToPdfFormProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  const inputRef = useRef<HTMLInputElement>(null)
  const itemsRef = useRef<ImageFileItem[]>([])
  const resultUrlRef = useRef<string | null>(null)
  const [items, setItems] = useState<ImageFileItem[]>([])
  const [dragging, setDragging] = useState(false)
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const total = useMemo(() => totalBytes(items), [items])
  const tooLarge = total > MAX_TOTAL_BYTES
  const config = MODE_CONFIG[mode]
  const copy = t[config.copyKey]
  const analyticsKey = getUtilityDefinition(config.utilityKey).analyticsKey

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    return () => {
      itemsRef.current.forEach(cleanupItem)
      if (resultUrlRef.current) {
        URL.revokeObjectURL(resultUrlRef.current)
      }
    }
  }, [])

  function clearResult() {
    if (resultUrlRef.current) {
      URL.revokeObjectURL(resultUrlRef.current)
      resultUrlRef.current = null
    }
    setPhase({ kind: 'idle' })
  }

  function addFiles(files: FileList | File[]) {
    const nextFiles = Array.from(files)
    if (nextFiles.length === 0) return

    clearResult()
    const supported = nextFiles.filter((file) => isSupportedImage(file, config.mode))
    const unsupportedCount = nextFiles.length - supported.length

    if (unsupportedCount > 0) {
      setPhase({
        kind: 'error',
        errorCode: 'unsupported_type',
        message: copy.unsupportedFiles,
      })
      toolError(analyticsKey, 'unsupported_type', { unsupported_count: unsupportedCount })
    }

    if (supported.length === 0) return
    setItems((current) => [...current, ...supported.map((file) => createImageItem(file, mode))])
  }

  function removeItem(id: string) {
    clearResult()
    setItems((current) => {
      const item = current.find((candidate) => candidate.id === id)
      if (item) cleanupItem(item)
      return current.filter((candidate) => candidate.id !== id)
    })
  }

  function moveItem(index: number, direction: -1 | 1) {
    clearResult()
    setItems((current) => {
      const nextIndex = index + direction
      if (nextIndex < 0 || nextIndex >= current.length) return current
      const copyItems = [...current]
      const [item] = copyItems.splice(index, 1)
      if (!item) return current
      copyItems.splice(nextIndex, 0, item)
      return copyItems
    })
  }

  async function handleSubmit() {
    if (items.length === 0 || tooLarge || phase.kind === 'loading') return

    clearResult()
    setPhase({ kind: 'loading' })
    const startMs = Date.now()
    toolInvoke(analyticsKey, {
      file_count: items.length,
      input_bytes: total,
    })

    try {
      const blob = await imagesToPdf(items.map((item) => item.file), config.mode)
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)
      const filename = outputFilename(items, config.fallbackFilename)
      resultUrlRef.current = objectUrl

      toolSuccess(analyticsKey, durationMs, {
        file_count: items.length,
        input_bytes: total,
        output_bytes: blob.size,
      })

      setPhase({ kind: 'success', blob, objectUrl, filename, durationMs })
    } catch (error) {
      const classifiedError = errorMessage(copy, error)
      toolError(analyticsKey, classifiedError.code, {
        file_count: items.length,
        input_bytes: total,
      })
      setPhase({
        kind: 'error',
        errorCode: classifiedError.code,
        message: classifiedError.message,
      })
    }
  }

  const disabled = phase.kind === 'loading'

  return (
    <Card className="mb-10">
      <CardContent className="space-y-6 pt-6">
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={copy.pickImages}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(event) => {
            if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
              inputRef.current?.click()
            }
          }}
          onDragOver={(event) => {
            event.preventDefault()
            if (!disabled) setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            if (!disabled) addFiles(event.dataTransfer.files)
          }}
          className={cn(
            'flex cursor-pointer select-none flex-col items-center justify-center gap-3',
            'rounded-xl border-2 border-dashed p-6 sm:p-8 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
            dragging && !disabled
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-accent/30',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept={config.accept}
            multiple
            className="hidden"
            onChange={(event) => {
              addFiles(event.target.files ?? [])
              event.currentTarget.value = ''
            }}
          />
          <div className="text-2xl font-semibold tracking-normal text-muted-foreground" aria-hidden>
            {config.dropLabel}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              <FilePickPrompt drag={copy.dropImages} />
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {format(copy.limit, { size: MAX_TOTAL_MB })}
            </p>
          </div>
        </div>

        {items.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <p className="font-medium text-foreground">
                {format(copy.selectedCount, { count: items.length })}
              </p>
              <p className={cn('text-muted-foreground', tooLarge && 'text-destructive')}>
                {formatBytes(total)}
              </p>
            </div>

            <ul className="space-y-2">
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className="flex items-center gap-3 rounded-lg border bg-background p-3"
                >
                  {item.previewUrl ? (
                    <img
                      src={item.previewUrl}
                      alt=""
                      className="h-12 w-12 rounded-md object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-muted text-xs font-semibold text-muted-foreground">
                      HEIC
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {item.file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(item.file.size)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={disabled || index === 0}
                      onClick={() => moveItem(index, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={disabled || index === items.length - 1}
                      onClick={() => moveItem(index, 1)}
                    >
                      ↓
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => removeItem(item.id)}
                    >
                      {t.common.deleteFile}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tooLarge && (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {format(copy.tooLarge, { size: MAX_TOTAL_MB })}
          </p>
        )}

        {phase.kind === 'error' && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
            <p className="font-medium text-destructive">{t.common.errorTitle}</p>
            <p className="mt-1 text-sm text-destructive/90">{phase.message}</p>
          </div>
        )}

        {phase.kind === 'success' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-950 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
            <p className="font-medium">{t.common.done}</p>
            <p className="mt-1 text-sm">
              {format(copy.successDetails, {
                size: formatBytes(phase.blob.size),
              })}
            </p>
            <Button asChild className="mt-4">
              <a href={phase.objectUrl} download={phase.filename}>
                {copy.downloadPdf}
              </a>
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            disabled={items.length === 0 || tooLarge || disabled}
            onClick={handleSubmit}
          >
            {phase.kind === 'loading' ? copy.processing : copy.convert}
          </Button>
          {items.length > 0 && (
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={() => {
                clearResult()
                items.forEach(cleanupItem)
                setItems([])
              }}
            >
              {copy.clear}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
